import { useMutation, useQuery, useQueries, useQueryClient } from 'react-query';
import { useMemo } from 'react';
import request from 'app/api/request';
import { errorHandler, showMessage } from 'app/utils/messages';
import { i18n } from 'app/utils/i18n';

export const queryKeys = {
  image: 'image',
  images: 'images',
};

export const useImage = (revisionId, type = 'thumbnail') =>
  useQuery(
    [queryKeys.image, revisionId],
    async () => {
      if (!revisionId) {
        return null;
      }
      const response = await request.get(`/gaby/images/${revisionId}/${type}`);
      return { file: response.data.file, metadata: response.data.originalMetadata, occurences: response.data.occurences };
    },
    {
      onError: errorHandler,
      enabled: !!revisionId,
    }
  );

export const useImages = (images = []) =>
  useQuery(
    [queryKeys.images],
    async () => {
      return await Promise.all(
        images.map(async (image) => {
          return request.get(`/gaby/images/${image.revisionId}/thumbnail`).then((res) => res.data);
        })
      );
    },
    {
      onError: errorHandler,
    }
  );

export const useBatchImages = (documentSuperId, revisionIds = [], imageType = 'thumbnail') => {
  const CHUNK_SIZE = 20;

  const chunks = useMemo(() => {
    if (!revisionIds.length) return [];
    const result = [];
    for (let i = 0; i < revisionIds.length; i += CHUNK_SIZE) {
      result.push(revisionIds.slice(i, i + CHUNK_SIZE));
    }
    return result;
  }, [revisionIds]);

  const queries = useQueries(
    chunks.map((chunk, index) => ({
      queryKey: [queryKeys.images, documentSuperId, imageType, index, chunk],
      queryFn: async () => {
        const res = await request.post('/gaby/images/batch', {
          revisionIds: chunk,
          documentSuperId,
          imageType,
        });
        return res.data;
      },
      enabled: !!documentSuperId && chunk.length > 0,
      onError: errorHandler,
      refetchOnWindowFocus: false,
    }))
  );

  const imageDataMap = useMemo(() => {
    return queries.reduce((acc, query) => {
      if (query.data) {
        query.data.forEach((img) => {
          acc[img.file.revisionId] = {
            file: img.file,
            metadata: img.originalMetadata,
            occurences: img.occurences,
          };
        });
      }
      return acc;
    }, {});
  }, [queries]);

  const isLoading = queries.some((q) => q.isLoading);
  const isFetching = queries.some((q) => q.isFetching);

  return { data: imageDataMap, isLoading, isFetching };
};
export const useImagesInDocument = (documentId = '') =>
  useQuery(
    [queryKeys.images],
    async () => {
      const response = await request.get(`/gaby/images/document/${documentId}/thumbnail`);
      return response.data;
    },
    {
      onError: errorHandler,
    }
  );

export const useChangeImageName = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ documentSuperId, imageSuperId, imageRevisionId, newName }) => {
      return request.post(`/gaby/documents/${documentSuperId}/actions`, {
        action: 'rename_image',
        superId: imageSuperId,
        revisionId: imageRevisionId,
        newName: newName,
      });
    },
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries([queryKeys.image, variables.imageRevisionId]);
        showMessage('success', i18n('image-queries.image-rename-success'));
      },
      onError: errorHandler,
    }
  );
};

export const useDeleteImage = () => {
  return useMutation(({ documentSuperId, imageRevisionId }) => {
    return request.post(`/gaby/documents/${documentSuperId}/actions`, {
      action: 'delete_image',
      revisionId: imageRevisionId,
    });
  });
};
